import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Switch, Typography, Slider, RadioGroup, FormControlLabel, Radio, IconButton, Box, Input, InputAdornment } from '@material-ui/core';
import CircularRange from '../../Signet/Studio/components/CircularRange';
import { useDesignPatternStudio } from '../DesignPatternStudioContext';
import { formatScaleValue, getValueLabelFormat } from '../../Signet/Studio/utils/formatter';
import { useHistory } from 'react-router-dom';
import EditIcon from '@material-ui/icons/Edit';
import PreviewVariantCard from './PreviewVariantCard';
import PositionGrid from './PositionGrid';
import GridOverlay from './GridOverlay';

const useStyles = makeStyles((theme) => ({
  widthSection: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  widthHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  editButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  widthName: {
    fontWeight: 800,
    textTransform: 'uppercase',
    fontSize: '1.2rem',
    display: 'flex',
    alignItems: 'flex-end',
  },
  sliderContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto 60px 1fr',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  sliderLabel: {
    whiteSpace: 'nowrap',
    minWidth: 75,
  },
  scaleBadge: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    fontWeight: 500,
    minWidth: 60,
    textAlign: 'center',
  },
  sliderWrapper: {
    width: '100%',
    paddingLeft: theme.spacing(1),
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  previewContainer: {
    marginTop: theme.spacing(2),
    position: 'relative',
    border: '1px solid #cccccc3b',
  },
  shadowContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  shadowLabel: {
    fontWeight: 500,
    alignSelf: 'center',
  },
  radioGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  radioOption: {
    marginRight: theme.spacing(1.5),
  },
  rotationContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  rotationLabel: {
    fontWeight: 500,
  },
  widthUnit: {
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'lowercase',
  },
  positionContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  noPreviewContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    color: theme.palette.text.secondary,
    minHeight: 250,
    textAlign: 'center',
  },
}));

const markers = [
  { value: -250, position: "0%" },
  { value: -200, position: "10%" },
  { value: -150, position: "20%" },
  { value: -100, position: "30%" },
  { value: -50, position: "40%" },
  { value: 0, position: "50%" },
  { value: 50, position: "60%" },
  { value: 100, position: "70%" },
  { value: 150, position: "80%" },
  { value: 200, position: "90%" },
  { value: 250, position: "100%" },
];

const NumberInput = ({ value, onChange, ...props }) => {
  const handleChange = (e) => {
    const numValue = parseFloat(e.target.value);
    if (!isNaN(numValue)) {
      onChange(numValue);
    }
  };
  return <Input
    value={value}
    onChange={handleChange}
    disableUnderline
    inputProps={{
      step: 1,
      type: 'number',
      style: { textAlign: 'center', padding: 0, height: '100%', boxSizing: 'border-box' }
    }}
    style={{
      height: '100%',
      width: '100%',
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
      margin: 0,
      fontSize: 'inherit',
      fontWeight: 'inherit',
      color: 'inherit',
    }}
    {...props}
  />
};


const WidthCard = ({
  profile,
  width,
  widthConfig,
  profileEnabled,
  onWidthToggle,
  onScaleChange,
  onPaddingChange,
  onShadowChange,
  onRotationChange,
  onWarpChange,
  onDisplacementChange,
  onPositionChange,
  onManualWarpToggle,
  onManualWarpUpdate,
}) => {
  const classes = useStyles();
  const { state, configMapping } = useDesignPatternStudio();
  const history = useHistory();

  const mapping = useMemo(() => configMapping?.[profile.sku_code]?.find(c => c.width.sku_code === width.sku_code), [configMapping, profile, width]);

  const handlePositionChange = (newX, newY) => {
    onPositionChange(profile.sku_code, width.sku_code, newX, newY);
  };

  const resetScale = () => {
    onScaleChange(profile.sku_code, width.sku_code, 0);
  };

  const updateManualWarp = (data) => {
    onManualWarpUpdate(profile.sku_code, width.sku_code, data);
  };

  return (
    <div className={classes.widthSection}>
      <div className={classes.widthHeader}>
        <Switch
          checked={width.checked}
          onChange={(e) => onWidthToggle(profile.id, width.id, e.target.checked)}
          color="primary"
          disabled={!profileEnabled}
        />
        <div className={classes.editButtonContainer}>
            <IconButton
                size="small"
                onClick={() => history.push(`/design-pattern-studio/edit-config?profileSku=${profile.sku_code}&widthSku=${width.sku_code}&source=studio`)}
                disabled={!width.checked || !profileEnabled}
            >
                <EditIcon />
            </IconButton>
            <Typography className={classes.widthName}>
              {width.sku_code}
              <Typography className={classes.widthUnit}>mm</Typography>
            </Typography>
        </div>
      </div>

      <div className={classes.sliderContainer}>
        <Typography variant="body2" className={classes.sliderLabel}>
          Scale <small>(%)</small>
        </Typography>
        <span className={classes.scaleBadge}>
          {/* {formatScaleValue(widthConfig?.scale || 0, !width.checked || !profileEnabled)} */}
          <NumberInput
            value={widthConfig?.scale || 0}
            onChange={(value) => onScaleChange(profile.sku_code, width.sku_code, value)}
            disabled={!width.checked || !profileEnabled || widthConfig?.manualWarp?.isEnabled}
            inputProps={{
              step: 1,
              type: 'number',
              style: { textAlign: 'center', padding: 0, height: '100%', boxSizing: 'border-box' }
            }}
          />
        </span>
        <div className={classes.sliderWrapper}>
          <Slider
            value={widthConfig?.scale || 0}
            onChange={(_, value) => onScaleChange(profile.sku_code, width.sku_code, value)}
            min={-100}
            max={100}
            step={1}
            disabled={!width.checked || !profileEnabled || widthConfig?.manualWarp?.isEnabled}
            valueLabelDisplay="auto"
            valueLabelFormat={getValueLabelFormat}
          />
        </div>
      </div>

      <div className={classes.sliderContainer}>
        <Typography variant="body2" className={classes.sliderLabel}>
          Padding <small>(%)</small>
        </Typography>
        <span className={classes.scaleBadge}>
          {/* {formatScaleValue(widthConfig?.padding || 0, !width.checked || !profileEnabled)} */}
          <NumberInput
            value={widthConfig?.padding || 0}
            onChange={(value) => onPaddingChange(profile.sku_code, width.sku_code, value)}
            disabled={!width.checked || !profileEnabled}
            inputProps={{
              step: 1,
              type: 'number',
              style: { textAlign: 'center', padding: 0, height: '100%', boxSizing: 'border-box' }
            }}
          />
        </span>
        <div className={classes.sliderWrapper}>
          <Slider
            value={widthConfig?.padding || 0}
            onChange={(_, value) => onPaddingChange(profile.sku_code, width.sku_code, value)}
            min={0}
            max={50}
            step={1}
            disabled={!width.checked || !profileEnabled}
            valueLabelDisplay="auto"
            valueLabelFormat={getValueLabelFormat}
          />
        </div>
      </div>

      <div className={classes.sliderContainer}>
        <Typography variant="body2" className={classes.sliderLabel}>
          Warp <small>(%)</small>
        </Typography>
        <span className={classes.scaleBadge}>
          {/* {formatScaleValue(widthConfig?.warp || 0, !width.checked || !profileEnabled)} */}
          <NumberInput
            value={widthConfig?.warp || 0}
            onChange={(value) => onWarpChange(profile.sku_code, width.sku_code, value)}
            disabled={!width.checked || !profileEnabled}
            inputProps={{
              step: 1,
              type: 'number',
              style: { textAlign: 'center', padding: 0, height: '100%', boxSizing: 'border-box' }
            }}
          />
        </span>
        <div className={classes.sliderWrapper}>
          <Slider
            value={widthConfig?.warp || 0}
            onChange={(_, value) => onWarpChange(profile.sku_code, width.sku_code, value)}
            min={-50}
            max={50}
            step={1}
            disabled={!width.checked || !profileEnabled}
            valueLabelDisplay="auto"
            valueLabelFormat={getValueLabelFormat}
          />
        </div>
      </div>
      <div className={classes.sliderContainer}>
        <Typography variant="body2" className={classes.sliderLabel}>
          Displacement <small>(%)</small>
        </Typography>
        <span className={classes.scaleBadge}>
          <NumberInput
            value={widthConfig?.displacement || 50}
            onChange={(value) => onDisplacementChange(profile.sku_code, width.sku_code, value)}
            disabled={!width.checked || !profileEnabled}
            inputProps={{
              min: 1,
              max: 50,
              step: 1,
              type: 'number',
              style: { textAlign: 'center', padding: 0, height: '100%', boxSizing: 'border-box' }
            }}
          />
        </span>
        <div className={classes.sliderWrapper}>
          <Slider
            value={widthConfig?.displacement || 50}
            onChange={(_, value) => onDisplacementChange(profile.sku_code, width.sku_code, value)}
            min={1}
            max={50}
            step={1}
            disabled={!width.checked || !profileEnabled}
            valueLabelDisplay="auto"
            valueLabelFormat={getValueLabelFormat}
          />
        </div>
      </div>
      <div className={classes.sliderContainer}>
        <Typography variant="body2" className={classes.sliderLabel}>
          Manual Warp
        </Typography>
        <span className={classes.scaleBadge} style={{ minWidth: 'auto', padding: 0 }}>
          <Switch
            checked={widthConfig?.manualWarp?.isEnabled || false}
            onChange={(e) => onManualWarpToggle && onManualWarpToggle(profile.sku_code, width.sku_code, e.target.checked)}
            disabled={!width.checked || !profileEnabled}
            color="primary"
            size="small"
          />
        </span>
        <div className={classes.sliderWrapper} style={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            {widthConfig?.manualWarp?.isEnabled ? 'Enabled - Use fullscreen editor to adjust control points' : 'Disabled - Use automatic warp slider above'}
          </Typography>
        </div>
      </div>
      <Box className={classes.positionContainer}>
        <Typography variant="subtitle2">
          Position Adjustment
        </Typography>
        <PositionGrid
          x={widthConfig?.shape_x || 0}
          y={widthConfig?.shape_y || 0}
          disabled={!width.checked || !profileEnabled || widthConfig?.manualWarp?.isEnabled}
          onPositionChange={handlePositionChange}
          markers={markers}
        />
      </Box>

      {/* shadow does not  for inside feature */}
      {!state.insideFeature && <div className={classes.shadowContainer}>
          <Typography variant="body2" className={classes.shadowLabel}>
            Shadow
          </Typography>
          <RadioGroup
            value={widthConfig?.depth || 'standard'}
            onChange={(e) => onShadowChange(profile.sku_code, width.sku_code, e.target.value)}
            className={classes.radioGroup}
            disabled={!width.checked || !profileEnabled}
          >
            <FormControlLabel
              value="shallow"
              className={classes.radioOption}
              control={<Radio size="small" />}
              label="Shallow"
              disabled={!width.checked || !profileEnabled}
            />
            <FormControlLabel
              value="standard"
              className={classes.radioOption}
              control={<Radio size="small" />}
              label="Standard"
              disabled={!width.checked || !profileEnabled}
            />
            <FormControlLabel
              value="deep"
              className={classes.radioOption}
              control={<Radio size="small" />}
              label="Deep"
              disabled={!width.checked || !profileEnabled}
            />
          </RadioGroup>
        </div>
      }

      <div className={classes.rotationContainer}>
        <Typography variant="body2" className={classes.rotationLabel}>
          Rotation
        </Typography>
        <span className={classes.scaleBadge}>
          <NumberInput
            value={widthConfig?.shape_rotation || 0}
            onChange={(value) => onRotationChange(profile.sku_code, width.sku_code, value)}
            disabled={!width.checked || !profileEnabled}
            inputProps={{
              min: 0,
              max: 360,
              step: 1,
              type: 'number',
              style: { textAlign: 'center', padding: 0, height: '100%', boxSizing: 'border-box' }
            }}
          />
        </span>
        <CircularRange
          value={widthConfig?.shape_rotation || 0}
          onChange={(value) => onRotationChange(profile.sku_code, width.sku_code, value)}
          disabled={!width.checked || !profileEnabled}
          size={80}
          strokeWidth={4}
        />
      </div>

      <div className={classes.previewContainer}>
        {state.svgFile && width.checked && profileEnabled ? (
          <>
            <PreviewVariantCard
              mapping={mapping}
              isPreview={true}
              config={widthConfig}
              isLarge={true}
              svgFile={state.svgFile}
              resetScale={resetScale}
              updateManualWarp={updateManualWarp}
            />
            <GridOverlay spacing={24} />
          </>
        ) : <Box className={classes.noPreviewContainer}><Typography variant="h6">Enable this width and upload an SVG to see the preview</Typography></Box>}
      </div>
    </div>
  );
};

export default WidthCard;
